import { useState, useEffect } from "react";

export const useSessionState = <T>(
  key: string,
  initialState: T,
  expiryMs: number = 5 * 60 * 1000, // Default expiry: 5 minutes
) => {
  const loadState = (): T => {
    const stored = sessionStorage.getItem(key);
    if (!stored) return initialState;

    try {
      const { value, timestamp } = JSON.parse(stored);
      if (Date.now() - timestamp > expiryMs) {
        sessionStorage.removeItem(key);
        return initialState;
      }
      // If initialState is a Date, convert the stored value back to a Date
      if (initialState instanceof Date) {
        return new Date(value) as T;
      } else {
        return value;
      }
    } catch {
      return initialState;
    }
  };

  const [state, setState] = useState<T>(loadState);

  useEffect(() => {
    sessionStorage.setItem(
      key,
      JSON.stringify({
        value: state instanceof Date ? state.toISOString() : state,
        timestamp: Date.now(),
      }),
    );
  }, [key, state]);

  return [state, setState] as const;
};
